import { assertRelativeDocumentPath } from "./document.ts";
import { EditorHostError, isEditorHostError } from "./errors.ts";
import {
  SELECTION_ITEM_KINDS,
  SELECTION_SCHEMA,
  SELECTION_SOURCES,
  SELECTION_SURFACES,
  type SelectionBBox,
  type SelectionItem,
  type SelectionItemKind,
  type SelectionSnapshot,
  type SelectionSource,
  type SelectionSurface,
} from "./types.ts";

const ITEM_KINDS = new Set<string>(SELECTION_ITEM_KINDS);
const SURFACES = new Set<string>(SELECTION_SURFACES);
const SOURCES = new Set<string>(SELECTION_SOURCES);
const SNAPSHOT_KEYS = new Set([
  "schema",
  "at",
  "surface",
  "document_path",
  "git_oid",
  "items",
  "source",
]);
const ITEM_KEYS = new Set([
  "kind",
  "refdes",
  "uuid",
  "value",
  "lib_id",
  "net",
  "sheet_path",
  "bbox",
]);
const FORBIDDEN_EXTRA_KEYS = new Set(["__proto__", "constructor", "prototype", "toJSON"]);

export function isoNow(now?: () => Date | string): string {
  const value = now ? now() : new Date();
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw invalidSnapshot("clock produced an invalid Date");
    }
    return value.toISOString();
  }
  if (typeof value === "string" && isIsoTimestamp(value)) return value;
  throw invalidSnapshot("clock must return a Date or ISO-8601 timestamp");
}

export function createClearedSnapshot(at?: string): SelectionSnapshot {
  const when = at === undefined ? isoNow() : at;
  if (typeof when !== "string" || !isIsoTimestamp(when)) {
    throw invalidSnapshot("at must be an ISO-8601 timestamp", { at: when });
  }
  return parseSelectionSnapshot({
    schema: SELECTION_SCHEMA,
    at: when,
    surface: "none",
    document_path: null,
    git_oid: null,
    items: [],
    source: "cleared",
  });
}

/** Document is loaded; copper items stay empty until a viewer select event. */
export function createDocumentSnapshot(opts: {
  at?: string;
  documentPath: string;
  gitOid?: string | null;
  surface: SelectionSurface;
}): SelectionSnapshot {
  const when = opts.at === undefined ? isoNow() : opts.at;
  if (opts.surface === "none") {
    throw invalidSnapshot("document snapshots cannot use surface none");
  }
  return parseSelectionSnapshot({
    schema: SELECTION_SCHEMA,
    at: when,
    surface: opts.surface,
    document_path: opts.documentPath,
    git_oid: opts.gitOid ?? null,
    items: [],
    source: "deep_link",
  });
}

export function isClearedSnapshot(snapshot: SelectionSnapshot): boolean {
  return (
    snapshot.schema === SELECTION_SCHEMA &&
    snapshot.surface === "none" &&
    snapshot.source === "cleared" &&
    snapshot.items.length === 0 &&
    snapshot.document_path === null
  );
}

export function cloneSelectionSnapshot(snapshot: SelectionSnapshot): SelectionSnapshot {
  try {
    return parseSelectionSnapshot(JSON.parse(JSON.stringify(snapshot)));
  } catch (err) {
    if (isEditorHostError(err)) throw err;
    throw invalidSnapshot("selection snapshot is not JSON-serializable");
  }
}

export function isSelectionSnapshot(value: unknown): value is SelectionSnapshot {
  try {
    parseSelectionSnapshot(value);
    return true;
  } catch {
    return false;
  }
}

export function parseSelectionSnapshot(input: unknown): SelectionSnapshot {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw invalidSnapshot("selection snapshot must be an object");
  }
  const raw = input as Record<string, unknown>;
  if (raw.schema !== SELECTION_SCHEMA) {
    throw invalidSnapshot(`schema must be ${SELECTION_SCHEMA}`, { schema: raw.schema });
  }
  if (typeof raw.at !== "string" || !isIsoTimestamp(raw.at)) {
    throw invalidSnapshot("at must be an ISO-8601 timestamp", { at: raw.at });
  }
  if (typeof raw.surface !== "string" || !SURFACES.has(raw.surface)) {
    throw invalidSnapshot("surface must be schematic | pcb | none", { surface: raw.surface });
  }
  if (typeof raw.source !== "string" || !SOURCES.has(raw.source)) {
    throw invalidSnapshot("source is not a known SelectionSource", { source: raw.source });
  }
  const document_path = parseDocumentPath(raw.document_path);
  if (raw.git_oid !== null && typeof raw.git_oid !== "string") {
    throw invalidSnapshot("git_oid must be a string or null");
  }
  if (!Array.isArray(raw.items)) {
    throw invalidSnapshot("items must be an array");
  }

  const items = raw.items.map((item, index) => parseSelectionItem(item, index));
  const surface = raw.surface as SelectionSurface;
  const source = raw.source as SelectionSource;

  if (surface === "none" && items.length > 0) {
    throw invalidSnapshot("surface none cannot include selection items");
  }
  if (source === "cleared" && (surface !== "none" || items.length > 0 || document_path !== null)) {
    throw invalidSnapshot("cleared snapshots must be surface none with empty items");
  }

  return {
    ...pickSafeExtras(raw, SNAPSHOT_KEYS),
    schema: SELECTION_SCHEMA,
    at: raw.at,
    surface,
    document_path,
    git_oid: raw.git_oid as string | null,
    items,
    source,
  };
}

function parseDocumentPath(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== "string") {
    throw invalidSnapshot("document_path must be a string or null");
  }
  try {
    return assertRelativeDocumentPath(value, "document_path");
  } catch (err) {
    if (isEditorHostError(err)) throw invalidSnapshot(err.message);
    throw err;
  }
}

function parseSelectionItem(input: unknown, index: number): SelectionItem {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw invalidSnapshot(`items[${index}] must be an object`);
  }
  const raw = input as Record<string, unknown>;
  if (typeof raw.kind !== "string" || !ITEM_KINDS.has(raw.kind)) {
    throw invalidSnapshot(`items[${index}].kind is not a known SelectionItemKind`, {
      kind: raw.kind,
    });
  }
  return {
    ...pickSafeExtras(raw, ITEM_KEYS),
    kind: raw.kind as SelectionItemKind,
    refdes: nullableString(raw.refdes, `items[${index}].refdes`),
    uuid: nullableString(raw.uuid, `items[${index}].uuid`),
    value: nullableString(raw.value, `items[${index}].value`),
    lib_id: nullableString(raw.lib_id, `items[${index}].lib_id`),
    net: nullableString(raw.net, `items[${index}].net`),
    sheet_path: nullableString(raw.sheet_path, `items[${index}].sheet_path`),
    ...(raw.bbox === undefined ? {} : { bbox: parseBBox(raw.bbox, index) }),
  };
}

function parseBBox(input: unknown, index: number): SelectionBBox {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw invalidSnapshot(`items[${index}].bbox must be an object`);
  }
  const raw = input as Record<string, unknown>;
  const x = finiteNumber(raw.x, `items[${index}].bbox.x`);
  const y = finiteNumber(raw.y, `items[${index}].bbox.y`);
  const w = finiteNumber(raw.w, `items[${index}].bbox.w`);
  const h = finiteNumber(raw.h, `items[${index}].bbox.h`);
  if (w < 0 || h < 0) {
    throw invalidSnapshot(`items[${index}].bbox w/h must be >= 0`);
  }
  return { x, y, w, h };
}

function pickSafeExtras(
  raw: Record<string, unknown>,
  reserved: Set<string>,
): Record<string, unknown> {
  const extras: Record<string, unknown> = {};
  for (const key of Object.keys(raw)) {
    if (reserved.has(key) || FORBIDDEN_EXTRA_KEYS.has(key)) continue;
    if (!isJsonSafe(raw[key])) continue;
    extras[key] = raw[key];
  }
  return extras;
}

function isJsonSafe(value: unknown): boolean {
  if (value === null) return true;
  const t = typeof value;
  if (t === "string" || t === "boolean") return true;
  if (t === "number") return Number.isFinite(value as number);
  if (t !== "object") return false;
  try {
    JSON.stringify(value);
    return true;
  } catch {
    return false;
  }
}

function nullableString(value: unknown, label: string): string | null {
  if (value === null) return null;
  if (typeof value === "string") return value;
  throw invalidSnapshot(`${label} must be a string or null`);
}

function finiteNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw invalidSnapshot(`${label} must be a finite number`);
  }
  return value;
}

export function isIsoTimestamp(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value)) {
    return false;
  }
  return !Number.isNaN(Date.parse(value));
}

function invalidSnapshot(message: string, details: Record<string, unknown> = {}): EditorHostError {
  return new EditorHostError("invalid_selection_snapshot", message, details);
}
